import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import Home from "../views/Home.vue";

Vue.use(VueRouter);

const routes = [
  {
    path: "",
    component: () => import("../layouts/DashboardLayout.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "/dashboard",
        name: "Dashboard",
        component: () => import("../views/Dashboard.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/amendments",
        name: "Amendments",
        component: () => import("../views/dashboard/Amendments.vue"),
        meta: { requiresAuth: true, requiresUserPermission: true },
      },
      {
        path: "/exprired-id-cards",
        name: "ExpiredIDCards",
        component: () => import("../views/dashboard/ExpiredIDCards.vue"),
        meta: { requiresAuth: true, requiresUserPermission: true },
      },
      {
        path: "/unapproved-id-cards",
        name: "UnapprovedIDCards",
        component: () => import("../views/dashboard/UnapprovedIDCards.vue"),
        meta: { requiresAuth: true, requiresUserPermission: true },
      },
      {
        path: "/expired-bonus",
        name: "ExpiredBonus",
        component: () => import("../views/dashboard/ExpiredBonus.vue"),
        meta: { requiresAuth: true, requiresUserPermission: true },
      },
      {
        path: "/support",
        name: "support",
        component: () => import("../views/dashboard/Support.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/law",
        name: "law",
        component: () => import("../views/dashboard/Law.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/id-card",
        name: "IdCard",
        component: () => import("../views/IdCard.vue"),
        meta: { requiresAuth: true, requiresUserPermission: true },
      },
      {
        path: "/organizations",
        name: "Organizations",
        component: () => import("../views/Organizations.vue"),
        meta: { requiresAuth: true, requiresUserPermission: true },
      },
      {
        path: "/bonus",
        name: "Bonus",
        component: () => import("../views/Bonus.vue"),
        meta: { requiresAuth: true, requiresUserPermission: true },
      },

      {
        path: "/users",
        name: "Users",
        component: () => import("../views/Users.vue"),
        meta: { requiresAuth: true, requiresAdminPermission: true },
      },
      {
        path: "/evaluations",
        name: "Evaluations",
        component: () => import("../views/Evaluations.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "Error403",
        name: "Error403",
        component: () => import("../views/Error403.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "Error402",
        name: "Error402",
        component: () => import("../views/Error402.vue"),
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: "/settings",
    name: "Settings",
    component: () => import("../layouts/SettingsLayout.vue"),
    children: [
      {
        path: "customer-address",
        name: "CustomerAddress",
        component: () => import("../views/settings/CustomerAddress.vue"),
        meta: { requiresAuth: true, requiresAdminPermission: true },
      },
      {
        path: "billing-address",
        name: "BillingAddress",
        component: () => import("../views/settings/BillingAddress.vue"),
        meta: { requiresAuth: true, requiresAdminPermission: true },
      },
      {
        path: "data-agreement",
        name: "DataAgreement",
        component: () => import("../views/settings/DataAgreement.vue"),
        meta: { requiresAuth: true, requiresAdminPermission: true },
      },
      {
        path: "id-card-layout",
        name: "IDCardLayout",
        component: () => import("../views/settings/IDCardLayout.vue"),
        meta: { requiresAuth: true, requiresAdminPermission: true },
      },
      {
        path: "additional-fields",
        name: "IdAdditionalFields",
        component: () => import("../views/settings/IdAdditionalFields.vue"),
        meta: { requiresAuth: true, requiresAdminPermission: true },
      },
      {
        path: "miscellaneous",
        name: "Miscellaneous",
        component: () => import("../views/settings/Miscellaneous.vue"),
        meta: { requiresAuth: true, requiresAdminPermission: true },
      },
    ],
  },

  // PUBLIC ROUTES
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/Login.vue"),
  },
  {
    path: "/login-callback",
    name: "LoginCallback",
    component: () => import("../views/LoginCallback.vue"),
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () => import("../views/ResetPassword.vue"),
  },
];

const router = new VueRouter({
  mode: "history",
  routes,
});

// CHECK AUTHENTICATION
import store from "@/store/index";

const PERMISSION_REFRESH_INTERVAL_MS = 60 * 1000;
let lastPermissionRefresh = 0;

async function refreshUserPermissionsThrottled() {
  const now = Date.now();
  if (now - lastPermissionRefresh < PERMISSION_REFRESH_INTERVAL_MS) {
    return;
  }
  lastPermissionRefresh = now;
  try {
    await store.dispatch("auth/fetchUserInfo");
  } catch (error) {
    // Navigation soll nicht an einem fehlgeschlagenen Rechte-Refresh scheitern
  }
}

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresAdminPermission = to.matched.some(
    (record) => record.meta.requiresAdminPermission,
  );
  const requiresUserPermission = to.matched.some(
    (record) => record.meta.requiresUserPermission,
  );

  // ✅ Route braucht KEIN Login
  if (!requiresAuth) {
    next();
    return;
  }

  let isAuthenticated = await store.dispatch("auth/isAuthenticated");

  // ✅ Falls nicht eingeloggt → Refresh versuchen (nur für lokale User)
  if (!isAuthenticated) {
    const isKeycloak = store.getters["auth/isKeycloakAuth"];

    if (!isKeycloak) {
      await store.dispatch("auth/refreshToken");
      isAuthenticated = await store.dispatch("auth/isAuthenticated");
    }

    if (!isAuthenticated) {
      next({ name: "Login" });
      return;
    }
  }

  // ✅ Ab hier ist der User eingeloggt

  // Rechte periodisch nachladen, damit neu vergebene Berechtigungen ohne
  // Neu-Login/Tab-Neustart wirksam werden (throttled, s. lastPermissionRefresh)
  if (requiresAdminPermission || requiresUserPermission) {
    await refreshUserPermissionsThrottled();
  }

  // ADMIN CHECK
  if (requiresAdminPermission) {
    const hasAdminPermission = await store.dispatch("auth/hasAdminPermission");

    if (hasAdminPermission) {
      next();
    } else {
      next({ name: "Error403" });
    }
    return;
  }

  // USER PERMISSION CHECK
  if (requiresUserPermission) {
    const hasUserPermission = await store.dispatch(
      "auth/hasUserPermission",
      to.name,
    );

    if (hasUserPermission) {
      next();
    } else {
      next({ name: "Error402" });
    }
    return;
  }

  next();
});

export default router;
